import * as z from "zod";
import { sameSettingsValue } from "./grouped-settings-changes";
import {
  brandingSettingsFromTeam,
  brandingSettingsShape,
  brandingTeamPatch,
} from "./portal/branding-settings";
import {
  bundlesSettingsFromTeam,
  bundlesSettingsShape,
  bundlesTeamPatch,
} from "./portal/bundles-settings";
import {
  customerOptionsSettingsFromTeam,
  customerOptionsSettingsShape,
  customerOptionsTeamPatch,
} from "./portal/customer-options-settings";
import {
  discountDistributionSettingsFromTeam,
  discountDistributionSettingsShape,
  discountDistributionTeamPatch,
} from "./portal/discount-distribution-settings";
import {
  displaySettingsFromTeam,
  displaySettingsShape,
  displayTeamPatch,
} from "./portal/display-settings";
import {
  exchangeAgentSettingsFromTeam,
  exchangeAgentSettingsShape,
  exchangeAgentTeamPatch,
} from "./portal/exchange-agent-settings";
import {
  exchangeUiSettingsFromTeam,
  exchangeUiSettingsShape,
  exchangeUiTeamPatch,
} from "./portal/exchange-ui-settings";
import {
  giftReturnsSettingsFromTeam,
  giftReturnsSettingsShape,
  giftReturnsTeamPatch,
} from "./portal/gift-returns-settings";
import type {
  PortalSettingsPatch,
  ReturnsPortalSettingsSource,
} from "./portal/portal-settings-source";
import {
  pricingSettingsFromTeam,
  pricingSettingsShape,
  pricingTeamPatch,
} from "./portal/pricing-settings";
import {
  returnEditingSettingsFromTeam,
  returnEditingSettingsShape,
  returnEditingTeamPatch,
} from "./portal/return-editing-settings";
import {
  shippingOptionsSettingsFromTeam,
  shippingOptionsSettingsShape,
  shippingOptionsTeamPatch,
} from "./portal/shipping-options-settings";
import {
  diffSettingsGroups,
  mergeSettingsGroups,
  type SettingsGroups,
} from "./tab-settings/settings-group-diff";

export const ReturnsPortalSettingsSchema = z.object({
  ...displaySettingsShape,
  ...brandingSettingsShape,
  ...exchangeAgentSettingsShape,
  ...exchangeUiSettingsShape,
  ...pricingSettingsShape,
  ...customerOptionsSettingsShape,
  ...returnEditingSettingsShape,
  ...discountDistributionSettingsShape,
  ...bundlesSettingsShape,
  ...shippingOptionsSettingsShape,
  ...giftReturnsSettingsShape,
});

export type ReturnsPortalSettings = z.infer<typeof ReturnsPortalSettingsSchema>;

const settingsShape = ReturnsPortalSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsPortalSettingsChangesSchema = z.object({
  display: settingsShape.display.partial().optional(),
  branding: settingsShape.branding.partial().optional(),
  exchangeAgent: settingsShape.exchangeAgent.partial().optional(),
  exchangeUi: settingsShape.exchangeUi.partial().optional(),
  pricing: settingsShape.pricing.partial().optional(),
  customerOptions: settingsShape.customerOptions.partial().optional(),
  returnEditing: settingsShape.returnEditing.partial().optional(),
  discountDistribution: settingsShape.discountDistribution.partial().optional(),
  bundles: settingsShape.bundles.partial().optional(),
  shippingOptions: settingsShape.shippingOptions.partial().optional(),
  giftReturns: settingsShape.giftReturns.partial().optional(),
});

export type ReturnsPortalSettingsChanges = z.infer<
  typeof ReturnsPortalSettingsChangesSchema
>;

export type ReturnsPortalTeamPatch = PortalSettingsPatch;

const SETTINGS_GROUP_KEYS = Object.keys(settingsShape);

function settingsGroups(settings: ReturnsPortalSettings): SettingsGroups {
  return {
    display: settings.display,
    branding: settings.branding,
    exchangeAgent: settings.exchangeAgent,
    exchangeUi: settings.exchangeUi,
    pricing: settings.pricing,
    customerOptions: settings.customerOptions,
    returnEditing: settings.returnEditing,
    discountDistribution: settings.discountDistribution,
    bundles: settings.bundles,
    shippingOptions: settings.shippingOptions,
    giftReturns: settings.giftReturns,
  };
}

export function returnsPortalSettingsFromTeam(
  source: ReturnsPortalSettingsSource,
): ReturnsPortalSettings {
  return {
    ...displaySettingsFromTeam(source),
    ...brandingSettingsFromTeam(source),
    ...exchangeAgentSettingsFromTeam(source),
    ...exchangeUiSettingsFromTeam(source),
    ...pricingSettingsFromTeam(source),
    ...customerOptionsSettingsFromTeam(source),
    ...returnEditingSettingsFromTeam(source),
    ...discountDistributionSettingsFromTeam(source),
    ...bundlesSettingsFromTeam(source),
    ...shippingOptionsSettingsFromTeam(source),
    ...giftReturnsSettingsFromTeam(source),
  };
}

/** Label deductions keep the legacy Shipping page's permission, since they move money. */
export function returnsPortalChangesNeedShippingPermission(
  changes: ReturnsPortalSettingsChanges,
): boolean {
  return !!changes.shippingOptions;
}

/** "Powered by Redo" in the portal is a Redo staff decision, never a merchant's. */
export function returnsPortalChangesNeedRedoStaff(
  changes: ReturnsPortalSettingsChanges,
): boolean {
  return Object.keys(changes.branding ?? {}).length > 0;
}

export function returnsPortalSettingsChanges(
  initial: ReturnsPortalSettings,
  next: ReturnsPortalSettings,
): ReturnsPortalSettingsChanges {
  return ReturnsPortalSettingsChangesSchema.parse(
    diffSettingsGroups(
      SETTINGS_GROUP_KEYS,
      settingsGroups(initial),
      settingsGroups(next),
    ),
  );
}

function applyChanges(
  base: ReturnsPortalSettings,
  changes: ReturnsPortalSettingsChanges,
): ReturnsPortalSettings {
  return ReturnsPortalSettingsSchema.parse(
    mergeSettingsGroups(SETTINGS_GROUP_KEYS, settingsGroups(base), changes),
  );
}

export function returnsPortalSettingsToTeamPatch(
  next: ReturnsPortalSettings,
): ReturnsPortalTeamPatch {
  const sections = [
    displayTeamPatch(next),
    brandingTeamPatch(next),
    exchangeAgentTeamPatch(next),
    exchangeUiTeamPatch(next),
    pricingTeamPatch(next),
    customerOptionsTeamPatch(next),
    returnEditingTeamPatch(next),
    discountDistributionTeamPatch(next),
    bundlesTeamPatch(next),
    shippingOptionsTeamPatch(next),
    giftReturnsTeamPatch(next),
  ];
  return {
    set: Object.assign({}, ...sections.map((section) => section.set)),
    unset: sections.flatMap((section) => section.unset),
  };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere, and a
 * change to one field still produces every legacy field its encoding spans.
 */
export function returnsPortalTeamPatchForChanges(
  current: ReturnsPortalSettingsSource,
  changes: ReturnsPortalSettingsChanges,
): ReturnsPortalTeamPatch {
  const stored = returnsPortalSettingsFromTeam(current);
  // Parsed like the request, so normalized values (exchange group ids) compare equal.
  const baseline = returnsPortalSettingsToTeamPatch(applyChanges(stored, {}));
  const requested = returnsPortalSettingsToTeamPatch(
    applyChanges(stored, changes),
  );

  return {
    set: Object.fromEntries(
      Object.entries(requested.set).filter(
        ([path, value]) =>
          !(path in baseline.set) ||
          !sameSettingsValue(baseline.set[path], value),
      ),
    ),
    unset: requested.unset.filter((path) => !baseline.unset.includes(path)),
  };
}
